import { BehaviorSubject, Subject, distinctUntilChanged, type Observable } from 'rxjs';
import { changeStream } from './routing';

/**
 * The open scene's saves are local-first: answered on the device, from its own copy of the scene's rows, and sent in
 * the background in the order they were made, online as well as offline. So an action that makes several saves in turn
 * (adding from the library, duplicating, swapping, splitting) never waits for the server between them. Undo and redo
 * are saves too, so they work as before. A save keeps the uniqueId it was made with, and a delete takes its
 * deletionUniqueId on the device, so a save sent twice is safe.
 *
 * A save the copy can't answer as the server would (a material, whose look and id the server works out; a change to a
 * row the copy doesn't hold) waits its turn and is answered by the server, unless the server can't be reached.
 * One that gets no answer, or an answer that may pass (a server error, the rate limit), is sent again, waiting longer
 * each time, until it is saved; meanwhile the editor says the change isn't saved yet. A send that takes longer than its
 * time limit counts as no answer, so a hung one never holds up the rest. One the server refuses is counted and the editor
 * reads its rows again; so is one the server answers with the same error (a 500, say, but never the gateway's or an
 * outage's 502, 503 or 504) MAX_SERVER_FAULTS times in a row, so it never blocks the saves behind it.
 *
 * The saves are the signed-in user's: when someone else signs in, or no one, those still waiting are dropped with the
 * copy, never sent with another user's bearer.
 *
 * While saves wait, reads are answered from the copy where it can, and any other request waits for them to be sent,
 * so it reaches the server after them, as it would have had it waited for each save. Offline, a read is also answered
 * with the last answer to the same read.
 */

type Row = { uniqueId: string } & Record<string, unknown>;
type Send = (request: Request) => Promise<Response>;

/** The v1 collections the editor keeps a copy of, with the name a deletion gives each. */
const COLLECTIONS = {
  'asset-instances': 'assetInstances',
  'clip-instances': 'clipInstances',
  keyframes: 'keyframes',
  'ui-element-instances': 'uiElementInstances',
  'material-instances': 'materialInstances',
  'vfx-instances': 'vfxInstances',
  scenes: 'scenes',
  stories: 'stories',
} as const;
type Collection = keyof typeof COLLECTIONS;
const isCollection = (name: string | undefined): name is Collection => !!name && name in COLLECTIONS;
const BY_DELETED_NAME = new Map(Object.entries(COLLECTIONS).map(([collection, name]) => [name as string, collection as Collection]));

/** A scene's content: what is made, deleted and brought back while editing. Scenes and stories are only changed. */
const CONTENT = new Set<Collection>(['asset-instances', 'clip-instances', 'keyframes', 'ui-element-instances', 'material-instances', 'vfx-instances']);

/** What a delete takes along, as the server's deletion graph does: the rows of `child` whose `field` names a row taken. */
const CASCADES: Array<{ parent: Collection; child: Collection; field: string }> = [
  { parent: 'asset-instances', child: 'keyframes', field: 'assetInstanceUniqueId' },
  { parent: 'asset-instances', child: 'clip-instances', field: 'assetInstanceUniqueId' },
  { parent: 'asset-instances', child: 'material-instances', field: 'assetInstanceUniqueId' },
  { parent: 'asset-instances', child: 'vfx-instances', field: 'assetInstanceUniqueId' },
  // A placement a clip spawned goes with the clip
  { parent: 'clip-instances', child: 'asset-instances', field: 'clipInstanceUniqueId' },
  { parent: 'ui-element-instances', child: 'clip-instances', field: 'uiElementInstanceUniqueId' },
];

/** What a delete leaves but points away from what it took: a clip triggered by it. A restore points it back. */
const DETACHES: Array<{ parent: Collection; child: Collection; field: string }> = [
  { parent: 'asset-instances', child: 'clip-instances', field: 'triggerAssetInstanceUniqueId' },
  { parent: 'ui-element-instances', child: 'clip-instances', field: 'triggerUiElementInstanceUniqueId' },
];

/** What the server gives a row it makes, for each field the save leaves out or null (its column defaults). */
const MADE_DEFAULTS: Partial<Record<Collection, Record<string, unknown>>> = {
  'asset-instances': {
    clipInstanceUniqueId: null,
    displayName: '',
    sourceName: 'Scan',
    position: '0, 0, 0',
    rotation: '0, 0, 0',
    scale: '1, 1, 1',
    altitude: '0',
    beginTime: 0,
    endTime: 0,
    displayOrder: -1,
    isLocked: false,
    isSelected: false,
    isBillboarded: false,
    hasPackedAlpha: false,
  },
  'clip-instances': { isLocked: false, keyframeType: 'linear' },
};

/** Every list a deletion's answer names, empty unless it took rows of that kind. */
const DELETED_NAMES = ['assetFiles', 'assetInstances', 'assetTypes', 'clipInstances', 'clipTypes', 'images', 'keyframes', 'materialInstances', 'materialTypes', 'projects', 'sceneTypes', 'scenes', 'stories', 'uiElementInstances', 'vfxInstances'];

/** The filters a list is answered by from the copy; a list filtered any other way is answered as it last was. */
const ROW_FILTERS = new Set(['sceneUniqueId', 'assetInstanceUniqueId', 'category', 'isActive']);
const PAGING = new Set(['limit', 'offset', 'sort']);

/** A read of a scene list page by page that has not reached its last page this long after it began is taken as abandoned. */
const LIST_WALK_MS = 60000;

/** How many other reads' last answers are kept. */
const MAX_ANSWERS = 300;

/** How long after a send that got no answer, or one that may pass, the waiting saves are sent again, doubled each time, up to the longest. */
export const SEND_AGAIN_MS = 2000;
const MAX_SEND_AGAIN_MS = 30000;

/** An answer that may pass: the session (refreshed meanwhile), a timeout, the rate limit, or the server. */
const mayPass = (status: number) => status === 401 || status === 408 || status === 425 || status === 429 || status >= 500;

/**
 * How long one send of a waiting save may take before it counts as no answer and is sent again (a save is safe to send
 * twice), doubled after each send that ran out of time, up to the longest, so a slow line still gets a big batch through.
 */
export const SEND_TIMEOUT_MS = 20000;
const MAX_SEND_TIMEOUT_MS = 120000;

/** A server error that waiting may not mend: any 5xx but the gateway's and an outage's (502, 503, 504), which pass. */
const serverFault = (status: number) => status >= 500 && status !== 502 && status !== 503 && status !== 504;

/** How many times in a row a save may get the same server fault before it is taken as refused and the next is sent. */
export const MAX_SERVER_FAULTS = 5;

const isTimeout = (error: unknown) => (error as { name?: unknown } | null)?.name === 'TimeoutError';

interface Route {
  collection: string;
  id?: string;
  action?: string;
}

/**
 * The rows a save changes (`collection:uniqueId`), each with the fields it changes, or '*' for a row it makes, deletes or
 * brings back.
 */
type Touches = Map<string, Set<string> | '*'>;

interface Waiting {
  method: string;
  url: string;
  headers: [string, string][];
  body: string | null;
  send: Send;
  /** Every row it changes (a batch's items, a placement's keyframes and clips, what a delete takes), and how. */
  touches: Touches;
  /** Made while the device was offline. */
  offline: boolean;
  /** Answered on the device in place of the server: what the server makes of it is read again once it is sent. */
  standIn: boolean;
  /** A save waiting its turn to be answered by the server: hands its caller the server's answer. */
  inTurn?: { route: SaveRoute; answer(response: Response | Error): void };
  /** The user who made it: it is sent only while they are signed in. */
  maker: string | null;
  /** Its number among the saves made (the last one merged into it), for landed. */
  seq: number;
  /** The server fault its sends got in a row, and how many times. */
  faults?: { status: number; count: number };
}

/** A row a delete pointed away from what it took, and what it pointed at. */
interface Detached {
  collection: Collection;
  uniqueId: string;
  field: string;
  was: unknown;
}

/** What a delete took, so undoing it brings it back. */
interface Taken {
  rows: Map<Collection, Row[]>;
  detached: Detached[];
}

/** What the saves came to once every waiting one was sent: how many saved, how many the server refused, and the rows answered on the device meanwhile (`collection:uniqueId`). */
export interface Synced {
  saved: number;
  refused: number;
  rows: string[];
  /** Some were made while the device was offline. */
  offline: boolean;
  /** Some were answered on the device in place of the server (offline, or while the server could not be reached): read the scene again. */
  reread: boolean;
}

export interface OfflineSavesOptions {
  /** The signed-in user, whom the server names as the maker of a placement. */
  userId?: () => string | null;
}

export interface OfflineSaves {
  /** Sends a v1 request: a save of the open scene is answered on the device and sent in the background. */
  fetch(request: Request, send: Send): Promise<Response>;
  /** The scene open in the editor, or null: only an open scene's saves are answered on the device. */
  open(sceneId: string | null): void;
  /** Whether the device has a connection, from the service monitor. */
  setOnline(online: boolean): void;
  /** How many saves are waiting to be sent. */
  waiting$: Observable<number>;
  /**
   * Whether saves wait and aren't going through: the device is offline, the one being sent keeps failing, or saves made
   * offline are still being sent. The editor says its changes aren't saved yet.
   */
  stalled$: Observable<boolean>;
  /** Each time every waiting save has been sent. */
  synced$: Observable<Synced>;
  /** How many saves have been made so far; landed takes it to wait for the ones made after. */
  made(): number;
  /**
   * Resolves once every save made so far has reached the server: true when none made after `since` (a made() count) was
   * refused or dropped. It waits as long as the saves do (offline, say).
   */
  landed(since: number): Promise<boolean>;
  /** The session may belong to someone else now (signed in or out): if so, the saves still waiting and the copy are dropped. */
  sessionChanged(): void;
}

const routeOf = (url: URL): Route | null => {
  const at = url.pathname.indexOf('/api/v1/');
  if (at < 0) return null;
  const [collection, id, action] = url.pathname.slice(at + '/api/v1/'.length).split('/').map(decodeURIComponent);
  return collection ? { collection, id, action } : null;
};

type SaveRoute = Route & { collection: Collection | 'deletions' };

/** Whether a request is a scene save that can be answered on the device and sent later. */
function isSave(method: string, route: Route | null): route is SaveRoute {
  if (!route) return false;
  // A restore, or a purge: purging a deletion made offline must reach the server after the delete it purges
  if (method === 'POST' && route.collection === 'deletions') return (!!route.id && route.action === 'restore') || (route.id === 'purge' && !route.action);
  if (!isCollection(route.collection) || route.action) return false;
  const content = CONTENT.has(route.collection);
  if (method === 'PATCH') return !!route.id && route.id !== 'batch';
  if (method === 'DELETE') return content && !!route.id;
  if (method === 'POST') return content && (!route.id || route.id === 'batch');
  return false;
}

const json = (body: unknown, status = 200) => new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });
const parse = (text: string | null): Record<string, unknown> => (text ? JSON.parse(text) : {});
const isRow = (value: unknown): value is Row => !!value && typeof value === 'object' && typeof (value as Row).uniqueId === 'string';
const rowsIn = (value: unknown): Row[] => (Array.isArray(value) ? value.filter(isRow) : []);

/** `row` with each default it leaves out or null. */
const withDefaults = <T extends Record<string, unknown>>(row: T, defaults: Record<string, unknown> = {}): T =>
  ({ ...row, ...Object.fromEntries(Object.entries(defaults).filter(([field]) => row[field] == null)) });

/** How long saves may take to reach the server before a file they replace is kept rather than deleted. */
export const LANDING_MS = 120_000;

/**
 * Whether every save made since `since` (a made() count) reached the server within `ms`: false when one was refused or
 * dropped, or they took longer. A file the saves stop pointing at is deleted only on true.
 */
export async function landedWithin(saves: Pick<OfflineSaves, 'landed'>, since: number, ms = LANDING_MS): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<boolean>(resolve => {
    timer = setTimeout(() => resolve(false), ms);
  });
  try {
    return await Promise.race([saves.landed(since), late]);
  } finally {
    clearTimeout(timer);
  }
}

export function offlineSaves({ userId = () => null }: OfflineSavesOptions = {}): OfflineSaves {
  const rows = new Map<string, Row>();
  // The scene each list of a collection was read for, so a later list for it can be answered from the copy
  const listed = new Set<string>();
  // Reads of a scene list page by page in flight, by the list without its offset: how many, and the stamp the first began at.
  // Each page read stamps its rows, so a row any of them saw since then is live, however the reads interleave
  const walks = new Map<string, { open: number; since: number; at: number }>();
  const lastListed = new Map<string, number>();
  let stamp = 0;
  // What each delete took, by its deletionUniqueId, so undoing it brings it back
  const gone = new Map<string, Taken>();
  const answers = new Map<string, { status: number; type: string; text: string }>();
  const waiting: Waiting[] = [];
  const waiting$ = new BehaviorSubject(0);
  const stalled$ = new BehaviorSubject(false);
  const synced$ = new Subject<Synced>();
  let sceneId: string | null = null;
  let online = true;
  let sending = false;
  // The save being sent, which no later one merges into
  let inFlight: Waiting | null = null;
  // The save being sent got no answer, or one that may pass, the last time it was sent
  let failing = false;
  let sendAgainTimer: ReturnType<typeof setTimeout> | undefined;
  let sendAgainMs = SEND_AGAIN_MS;
  let saved = 0;
  let refused = 0;
  let madeOffline = false;
  let stoodIn = false;
  // Requests that wait for the waiting saves to be sent (or for sending them to stall) before they go
  let held: Array<() => void> = [];
  // Rows answered on the device since the last sync: the server's may differ (its rounding, its defaults)
  const answered = new Set<string>();
  // A material made on the device takes an id here; the server makes its own, which later waiting saves are sent with
  const provisional = new Map<string, { assetInstanceUniqueId: unknown; targetNodeName: unknown; targetNodeIndex: unknown }>();
  const serverIds = new Map<string, string>();
  let sendTimeoutMs = SEND_TIMEOUT_MS;
  // The user the copy and the waiting saves are for; undefined until the first request
  let seenUser: string | null | undefined;
  // How many times the waiting saves were dropped: a send that answers after a drop changes nothing
  let drops = 0;
  // Saves made, the newest of them sent and answered, and the newest of them refused or dropped (landed)
  let madeCount = 0;
  let doneThrough = 0;
  let refusedThrough = 0;
  const landings: Array<{ since: number; through: number; resolve(saved: boolean): void }> = [];

  const key = (collection: Collection, id: string) => `${collection}:${id}`;
  // The service monitor's verdict alone: the browser's online flag can say offline on a connection that works
  const connected = () => online;
  // Once a save waits, the copy is ahead of the server: reads are answered from it where it can
  const ahead = () => !connected() || waiting.length > 0;
  // The server can't answer now: saves are answered on the device, and nothing waits for them to be sent
  const unreachable = () => !connected() || failing;
  const stalled = () => waiting.length > 0 && (unreachable() || waiting.some(save => save.offline));

  const changed = () => {
    waiting$.next(waiting.length);
    const now = stalled();
    if (stalled$.value !== now) stalled$.next(now);
    if (waiting.length === 0 || unreachable()) {
      const ready = held;
      held = [];
      ready.forEach(release => release());
    }
  };
  // Resolves once no save waits, or sending them can't go through now
  const whenSent = () => (waiting.length === 0 || unreachable() ? Promise.resolve() : new Promise<void>(resolve => held.push(resolve)));

  function settleLandings(): void {
    for (const landing of [...landings]) {
      if (landing.through > doneThrough) continue;
      landings.splice(landings.indexOf(landing), 1);
      landing.resolve(refusedThrough <= landing.since);
    }
  }

  // A save is done with, saved or not: it, and every save made before it, has been answered
  function finish(save: Waiting, saved: boolean): void {
    doneThrough = Math.max(doneThrough, save.seq);
    if (!saved) refusedThrough = Math.max(refusedThrough, save.seq);
    settleLandings();
  }

  // Someone else signed in, or no one: the last user's waiting saves are never sent, and nothing kept for them answers a read
  function dropAll(): void {
    drops += 1;
    const lost = waiting.splice(0);
    if (lost.length > 0) console.warn(`[offlineSaves] ${lost.length} waiting save(s) dropped: the user who made them is no longer signed in`);
    for (const save of lost) {
      refusedThrough = Math.max(refusedThrough, save.seq);
      save.inTurn?.answer(new TypeError('Failed to fetch: signed out'));
    }
    doneThrough = madeCount;
    settleLandings();
    // A send of the last user's still on its way finishes on its own; the next user's saves don't wait for it
    inFlight = null;
    sending = false;
    failing = false;
    clearTimeout(sendAgainTimer);
    sendAgainMs = SEND_AGAIN_MS;
    sendTimeoutMs = SEND_TIMEOUT_MS;
    saved = 0;
    refused = 0;
    madeOffline = false;
    stoodIn = false;
    for (const kept of [rows, listed, walks, lastListed, gone, answers, answered, provisional, serverIds]) kept.clear();
    changed();
  }

  function checkUser(): void {
    const user = userId();
    if (seenUser !== undefined && user !== seenUser) {
      seenUser = user;
      dropAll();
      return;
    }
    seenUser = user;
  }

  // One send of a waiting save, given up as no answer once it has taken sendTimeoutMs. The answer's body is read within
  // that time too, so one whose headers arrive and whose body then stalls counts as no answer, never a wait without end
  function sendInTime(save: Waiting, url: string, body: string | null): Promise<Response> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new DOMException(`No answer within ${sendTimeoutMs} ms`, 'TimeoutError'));
      }, sendTimeoutMs);
    });
    const sent = save.send(new Request(url, { method: save.method, headers: save.headers, body, signal: controller.signal }))
      .then(async response => {
        const read = await response.arrayBuffer();
        const empty = response.status === 204 || response.status === 205 || response.status === 304;
        return new Response(empty ? null : read, { status: response.status, statusText: response.statusText, headers: response.headers });
      });
    return Promise.race([sent, timedOut]).finally(() => clearTimeout(timer));
  }

  // A server fault that keeps coming back the same is the server's answer, not a passing one
  function refusedForGood(save: Waiting, status: number): boolean {
    if (!serverFault(status)) {
      delete save.faults;
      return false;
    }
    save.faults = { status, count: save.faults?.status === status ? save.faults.count + 1 : 1 };
    return save.faults.count >= MAX_SERVER_FAULTS;
  }

  const remember = (collection: Collection, row: Row) => rows.set(key(collection, row.uniqueId), row);
  const note = (collection: Collection, list: Row[]) => list.forEach(row => answered.add(key(collection, row.uniqueId)));
  // A row the server answers for is kept, unless it holds an edit answered on the device and not yet sent
  const learnRow = (collection: Collection, row: Row) => {
    if (!answered.has(key(collection, row.uniqueId))) remember(collection, row);
  };
  // The fields of a row the saves still waiting change, or '*' when one of them makes, deletes or brings it back
  function pendingOf(at: string): Set<string> | '*' | undefined {
    let pending: Set<string> | undefined;
    for (const save of waiting) {
      const fields = save.touches.get(at);
      if (fields === '*') return '*';
      if (fields) pending = new Set([...(pending ?? []), ...fields]);
    }
    return pending;
  }
  // A row a save's answer gives is kept, but for the fields the saves still waiting change: the copy holds those as the
  // server will once they are sent, so an answer to an earlier save never takes back a later one
  const rememberAnswered = (collection: Collection, row: Row) => {
    const at = key(collection, row.uniqueId);
    const pending = pendingOf(at);
    if (pending === '*') return;
    const current = rows.get(at);
    if (!pending || !current) return void remember(collection, row);
    remember(collection, { ...row, ...Object.fromEntries([...pending].filter(field => field in current).map(field => [field, current[field]])) });
  };
  const sceneOf = (row: Row): unknown =>
    row.sceneUniqueId ?? (typeof row.assetInstanceUniqueId === 'string' ? rows.get(key('asset-instances', row.assetInstanceUniqueId))?.sceneUniqueId : undefined);
  const rowsOf = (collection: Collection) => [...rows.entries()].filter(([at]) => at.startsWith(`${collection}:`)).map(([, row]) => row);

  // A placement answers with its asset instance and what was made with it
  function rememberMade(collection: Collection, answer: Record<string, unknown>, keepRow: (collection: Collection, row: Row) => void = remember): void {
    if (collection !== 'asset-instances' || !isRow(answer.assetInstance)) {
      if (isRow(answer)) keepRow(collection, answer);
      return;
    }
    keepRow('asset-instances', answer.assetInstance);
    rowsIn(answer.keyframes).forEach(row => keepRow('keyframes', row));
    rowsIn(answer.clipInstances).forEach(row => keepRow('clip-instances', row));
  }

  // A batch of changes answers none of its rows: each is the row as it was, with the change
  function rememberBatch(collection: Collection, body: Record<string, unknown>, keepRow: (collection: Collection, row: Row) => void = remember): void {
    for (const item of rowsIn(body.items)) keepRow(collection, { ...rows.get(key(collection, item.uniqueId)), ...item });
  }

  function take(deletionId: string, taken: Taken): void {
    for (const [collection, list] of taken.rows) list.forEach(row => rows.delete(key(collection, row.uniqueId)));
    for (const { collection, uniqueId, field } of taken.detached) {
      const row = rows.get(key(collection, uniqueId));
      if (row) remember(collection, { ...row, [field]: null });
    }
    gone.set(deletionId, taken);
  }

  const isGone = (collection: Collection, id: string) =>
    [...gone.values()].some(taken => taken.rows.get(collection)?.some(row => row.uniqueId === id));

  /** Brings back what a delete took, and points what it detached back where it still holds what the delete left; answers the rows it changed. */
  function bringBack(taken: Taken | undefined): Array<{ resource: string; uniqueId: string; fields: string[] }> {
    for (const [collection, list] of taken?.rows ?? []) list.forEach(row => remember(collection, row));
    const pointedBack = new Map<string, { resource: string; uniqueId: string; fields: string[] }>();
    for (const { collection, uniqueId, field, was } of taken?.detached ?? []) {
      const row = rows.get(key(collection, uniqueId));
      if (!row || row[field] != null) continue;
      remember(collection, { ...row, [field]: was });
      note(collection, [row]);
      const at = key(collection, uniqueId);
      const entry = pointedBack.get(at) ?? { resource: COLLECTIONS[collection], uniqueId, fields: [] };
      entry.fields.push(field);
      pointedBack.set(at, entry);
    }
    return [...pointedBack.values()];
  }

  /** What deleting this row takes along, and what it detaches, as the server's deletion graph does, from the copy. */
  function graphOf(collection: Collection, id: string): { rows: Map<Collection, Row[]>; ids: Map<Collection, Set<string>>; detached: Detached[] } {
    const ids = new Map<Collection, Set<string>>([[collection, new Set([id])]]);
    const found = new Map<Collection, Row[]>();
    const own = rows.get(key(collection, id));
    found.set(collection, own ? [own] : []);
    const queue: Array<[Collection, string]> = [[collection, id]];
    while (queue.length > 0) {
      const [parent, parentId] = queue.shift()!;
      for (const rule of CASCADES.filter(each => each.parent === parent)) {
        for (const row of rowsOf(rule.child)) {
          if (row[rule.field] !== parentId || ids.get(rule.child)?.has(row.uniqueId)) continue;
          if (!ids.has(rule.child)) ids.set(rule.child, new Set());
          ids.get(rule.child)!.add(row.uniqueId);
          found.set(rule.child, [...(found.get(rule.child) ?? []), row]);
          queue.push([rule.child, row.uniqueId]);
        }
      }
    }
    const detached: Detached[] = [];
    for (const rule of DETACHES) {
      const taken = ids.get(rule.parent);
      if (!taken) continue;
      for (const row of rowsOf(rule.child)) {
        const target = row[rule.field];
        if (typeof target !== 'string' || !taken.has(target) || ids.get(rule.child)?.has(row.uniqueId)) continue;
        detached.push({ collection: rule.child, uniqueId: row.uniqueId, field: rule.field, was: target });
      }
    }
    return { rows: found, ids, detached };
  }

  async function learn(method: string, url: URL, route: Route | null, body: string | null, response: Response): Promise<void> {
    if (!(response.headers.get('Content-Type') ?? '').includes('json')) return;
    if (method === 'GET' && response.status === 404 && route && isCollection(route.collection) && route.id && !route.action) {
      rows.delete(key(route.collection, route.id));
      return;
    }
    if (!response.ok) return;
    const text = await response.text();
    const answer = text ? JSON.parse(text) : null;
    if (method === 'GET') {
      if (route && isCollection(route.collection) && !route.action) {
        if (route.id && isRow(answer)) return void learnRow(route.collection, answer);
        if (!route.id && answer && Array.isArray(answer.items)) {
          const collection = route.collection as Collection;
          const items = rowsIn(answer.items);
          items.forEach(row => learnRow(collection, row));
          const scene = url.searchParams.get('sceneUniqueId');
          if (scene) listed.add(key(collection, scene));
          // A whole scene's list, read to its last page, says which of its rows are gone (deleted elsewhere); an edit not yet sent stays.
          // Once every read of the list in flight has finished, a row none of them saw since the first began is gone
          if (scene && [...url.searchParams.keys()].every(name => name === 'sceneUniqueId' || PAGING.has(name))) {
            const list = new URL(url);
            list.searchParams.delete('offset');
            stamp += 1;
            let walk = walks.get(list.href);
            if (Number(url.searchParams.get('offset') ?? 0) === 0) {
              if (!walk || Date.now() - walk.at > LIST_WALK_MS) walk = { open: 0, since: stamp, at: Date.now() };
              walk.open += 1;
              walks.set(list.href, walk);
            }
            items.forEach(row => lastListed.set(key(collection, row.uniqueId), stamp));
            if (answer.nextOffset != null || !walk) return;
            walk.open -= 1;
            if (walk.open > 0) return;
            walks.delete(list.href);
            for (const row of rowsOf(collection)) {
              const at = key(collection, row.uniqueId);
              if (sceneOf(row) === scene && (lastListed.get(at) ?? 0) < walk.since && !answered.has(at)) rows.delete(at);
            }
          }
          return;
        }
      }
      answers.delete(url.href);
      answers.set(url.href, { status: response.status, type: response.headers.get('Content-Type') ?? 'application/json', text });
      if (answers.size > MAX_ANSWERS) answers.delete(answers.keys().next().value as string);
      return;
    }
    if (!isSave(method, route)) return;
    if (route.collection === 'deletions' && route.id === 'purge') {
      for (const id of (answer?.purged ?? []) as string[]) gone.delete(id);
      return;
    }
    if (route.collection === 'deletions') {
      for (const [name, list] of Object.entries(answer ?? {})) {
        const collection = BY_DELETED_NAME.get(name);
        if (collection) rowsIn(list).forEach(row => rememberAnswered(collection, row));
      }
      gone.delete(route.id!);
      return;
    }
    const { collection } = route;
    if (method === 'DELETE') {
      const taken: Taken = { rows: new Map(), detached: [] };
      for (const [name, ids] of Object.entries(answer?.deleted ?? {})) {
        const kind = BY_DELETED_NAME.get(name);
        if (!kind || !Array.isArray(ids)) continue;
        taken.rows.set(kind, ids.map(id => rows.get(key(kind, String(id)))).filter(isRow));
      }
      // A row it left but changed (a clip's trigger cleared) is cleared in the copy too, and set back by its restore
      for (const change of (answer?.changed ?? []) as Array<{ resource?: string; uniqueId?: string; fields?: string[] }>) {
        const kind = change.resource ? BY_DELETED_NAME.get(change.resource) : undefined;
        const row = kind && change.uniqueId ? rows.get(key(kind, change.uniqueId)) : undefined;
        if (!kind || !row) continue;
        for (const field of change.fields ?? []) if (row[field] != null) taken.detached.push({ collection: kind, uniqueId: row.uniqueId, field, was: row[field] });
      }
      take(String(answer?.deletionUniqueId), taken);
      return;
    }
    if (route.id === 'batch') {
      if (collection === 'material-instances') rowsIn(answer).forEach(row => rememberAnswered('material-instances', row));
      else rememberBatch(collection, parse(body), rememberAnswered);
      return;
    }
    rememberMade(collection, answer ?? {}, rememberAnswered);
  }

  /**
   * A read answered from the copy, or null when it can't be: a row, or a scene's list filtered only by what the copy
   * holds. With `lastAnswer` (offline), any other read is answered as it last was.
   */
  function read(url: URL, route: Route | null, lastAnswer: boolean): Response | null {
    if (route && isCollection(route.collection) && !route.action) {
      if (route.id) {
        const row = rows.get(key(route.collection, route.id));
        if (row) return json(row);
        if (isGone(route.collection, route.id)) return json({ status: 404, title: 'Not Found', code: 'not_found' }, 404);
      } else {
        const filters = [...url.searchParams.keys()].filter(name => !PAGING.has(name));
        const scene = url.searchParams.get('sceneUniqueId') ?? sceneId;
        if (scene && listed.has(key(route.collection, scene)) && filters.every(name => ROW_FILTERS.has(name))) {
          const items = Number(url.searchParams.get('offset') ?? 0) > 0 ? [] : rowsOf(route.collection).filter(row =>
            sceneOf(row) === scene && filters.every(name => name === 'sceneUniqueId' || url.searchParams.getAll(name).includes(String(row[name]))));
          return json({ items, nextOffset: null, total: items.length });
        }
      }
    }
    const last = lastAnswer ? answers.get(url.href) : undefined;
    return last ? new Response(last.text, { status: last.status, headers: { 'Content-Type': last.type } }) : null;
  }

  /** Whether the copy answers this save as the server would, so it needn't wait for the server's answer. */
  function answersAsServer(method: string, route: SaveRoute): boolean {
    if (route.collection === 'deletions') return route.id === 'purge' || gone.has(route.id!);
    const { collection } = route;
    // The server works out a material's look, and makes the id of one new to its node; an effect is the server's to make
    if (collection === 'material-instances' && method === 'POST') return false;
    if (collection === 'vfx-instances' && method === 'POST') return false;
    if (method === 'POST') return true;
    // A change or a delete is answered from the row as the copy holds it
    return rows.has(key(collection, route.id!));
  }

  const fieldsOf = (item: Record<string, unknown>) => new Set(Object.keys(item).filter(field => field !== 'uniqueId'));

  /** The rows a save changes, from what it sends: what it makes whole, what it changes by the fields it sends. */
  function touchesOf(method: string, route: SaveRoute, body: string | null): Touches {
    const touches: Touches = new Map();
    if (route.collection === 'deletions') return touches;
    const { collection } = route;
    const sent = parse(body);
    if (method === 'PATCH') touches.set(key(collection, route.id!), new Set([...fieldsOf(sent), 'updatedAt']));
    else if (method === 'DELETE') touches.set(key(collection, route.id!), '*');
    else if (route.id === 'batch') rowsIn(sent.items).forEach(item => touches.set(key(collection, item.uniqueId), fieldsOf(item)));
    else {
      if (typeof sent.uniqueId === 'string') touches.set(key(collection, sent.uniqueId), '*');
      if (collection === 'asset-instances') {
        rowsIn(sent.keyframes).forEach(row => touches.set(key('keyframes', row.uniqueId), '*'));
        rowsIn(sent.clipInstances).forEach(row => touches.set(key('clip-instances', row.uniqueId), '*'));
      }
    }
    return touches;
  }

  /** What a delete takes, or a restore brings back, changes whole; what it points away or back, by that field. */
  function touchesOfGraph(ids: Iterable<[Collection, Iterable<string>]>, detached: Detached[]): Touches {
    const touches: Touches = new Map();
    for (const [collection, list] of ids) for (const id of list) touches.set(key(collection, id), '*');
    for (const { collection, uniqueId, field } of detached) {
      const at = key(collection, uniqueId);
      const fields = touches.get(at);
      if (fields !== '*') touches.set(at, new Set([...(fields ?? []), field]));
    }
    return touches;
  }

  /** Applies a save to the copy and answers it as the server would; the url and body are what is sent later, and touches the rows it changed. */
  function answer(method: string, url: URL, body: string | null, route: SaveRoute): { response: Response; url: string; body: string | null; touches: Touches } {
    const local = answerOnDevice(method, url, body, route);
    return { ...local, touches: local.touches ?? touchesOf(method, route, local.body) };
  }

  function answerOnDevice(method: string, url: URL, body: string | null, route: SaveRoute): { response: Response; url: string; body: string | null; touches?: Touches } {
    const now = new Date().toISOString();
    const sent = parse(body);
    if (route.collection === 'deletions' && route.id === 'purge') {
      const purged = ((sent.uniqueIds as string[] | null | undefined) ?? []).filter(id => gone.delete(id));
      return { response: json({ purged }), url: url.href, body };
    }
    if (route.collection === 'deletions') {
      const taken = gone.get(route.id!);
      if (!taken) throw new TypeError('Failed to fetch: that deletion is not known offline');
      const pointedBack = bringBack(taken);
      gone.delete(route.id!);
      for (const [kind, list] of taken.rows) note(kind, list);
      const restored: Record<string, unknown> = Object.fromEntries(DELETED_NAMES.map(name => [name, []]));
      for (const [kind, list] of taken.rows) restored[COLLECTIONS[kind]] = list;
      const touches = touchesOfGraph([...taken.rows].map(([kind, list]) => [kind, list.map(row => row.uniqueId)]), taken.detached);
      return { response: json({ ...restored, deletionUniqueId: route.id, changed: pointedBack }), url: url.href, body, touches };
    }
    const { collection } = route;
    if (method === 'PATCH') {
      const row = { ...rows.get(key(collection, route.id!)), ...sent, uniqueId: route.id!, updatedAt: now };
      remember(collection, row);
      note(collection, [row]);
      return { response: json(row), url: url.href, body };
    }
    if (method === 'DELETE') {
      const deletionId = url.searchParams.get('deletionUniqueId') ?? crypto.randomUUID();
      url.searchParams.set('deletionUniqueId', deletionId);
      const graph = graphOf(collection, route.id!);
      take(deletionId, { rows: graph.rows, detached: graph.detached });
      const deleted: Record<string, string[]> = Object.fromEntries(DELETED_NAMES.map(name => [name, []]));
      for (const [kind, ids] of graph.ids) deleted[COLLECTIONS[kind]].push(...ids);
      const detachedRows = new Map<string, { resource: string; uniqueId: string; fields: string[] }>();
      for (const { collection: kind, uniqueId, field } of graph.detached) {
        const at = key(kind, uniqueId);
        const entry = detachedRows.get(at) ?? { resource: COLLECTIONS[kind], uniqueId, fields: [] };
        entry.fields.push(field);
        detachedRows.set(at, entry);
        note(kind, [{ uniqueId }]);
      }
      return { response: json({ deletionUniqueId: deletionId, deleted, changed: [...detachedRows.values()] }), url: url.href, body, touches: touchesOfGraph(graph.ids, graph.detached) };
    }
    if (route.id === 'batch') {
      if (collection !== 'material-instances') {
        rememberBatch(collection, sent);
        note(collection, rowsIn(sent.items));
        return { response: new Response(null, { status: 204 }), url: url.href, body };
      }
      // A material is one per target; one new on the device takes an id here, and the server's own once it is sent and read again
      const touches: Touches = new Map();
      const upserted = rowsIn((sent.items as unknown[] | undefined)?.map(item => {
        const fields = { ...(item as Record<string, unknown>) };
        delete fields.shaderCodeName;
        const before = rowsOf('material-instances').find(row => row.assetInstanceUniqueId === fields.assetInstanceUniqueId
          && row.targetNodeName === fields.targetNodeName && row.targetNodeIndex === fields.targetNodeIndex);
        const uniqueId = before?.uniqueId ?? crypto.randomUUID();
        if (!before) provisional.set(uniqueId, { assetInstanceUniqueId: fields.assetInstanceUniqueId, targetNodeName: fields.targetNodeName, targetNodeIndex: fields.targetNodeIndex });
        touches.set(key('material-instances', uniqueId), before ? new Set([...fieldsOf(fields), 'updatedAt']) : '*');
        return { createdAt: now, ...before, ...fields, uniqueId, updatedAt: now };
      }));
      upserted.forEach(row => remember('material-instances', row));
      note('material-instances', upserted);
      return { response: json(upserted), url: url.href, body, touches };
    }
    // Made: it keeps its uniqueId, and so do the keyframes and clips a placement is made with
    const withId = <T extends Record<string, unknown>>(row: T) => ({ ...row, uniqueId: typeof row.uniqueId === 'string' ? row.uniqueId : crypto.randomUUID() });
    const made = withId(sent);
    if (collection === 'asset-instances') {
      const keyframes = ((made.keyframes as Record<string, unknown>[] | undefined) ?? []).map(withId);
      const clipInstances = ((made.clipInstances as Record<string, unknown>[] | undefined) ?? []).map(withId);
      const toSend = { ...made, ...(made.keyframes ? { keyframes } : {}), ...(made.clipInstances ? { clipInstances } : {}) };
      const placement: Record<string, unknown> = { ...made };
      delete placement.keyframes;
      delete placement.clipInstances;
      const placed = {
        assetInstance: { ...withDefaults(placement, MADE_DEFAULTS['asset-instances']), userUniqueId: userId(), createdAt: now, updatedAt: now },
        keyframes: keyframes.map(keyframe => ({ sceneUniqueId: made.sceneUniqueId, ...keyframe, assetInstanceUniqueId: made.uniqueId, createdAt: now })),
        clipInstances: clipInstances.map(clip => ({ ...withDefaults({ sceneUniqueId: made.sceneUniqueId, ...clip }, MADE_DEFAULTS['clip-instances']), assetInstanceUniqueId: made.uniqueId, updatedAt: now })),
      };
      rememberMade(collection, placed);
      note('asset-instances', [placed.assetInstance as unknown as Row]);
      note('keyframes', placed.keyframes as unknown as Row[]);
      note('clip-instances', placed.clipInstances as unknown as Row[]);
      return { response: json(placed), url: url.href, body: JSON.stringify(toSend) };
    }
    // A keyframe is in its placement's scene
    const scene = collection === 'keyframes' && typeof made.assetInstanceUniqueId === 'string'
      ? { sceneUniqueId: rows.get(key('asset-instances', made.assetInstanceUniqueId))?.sceneUniqueId ?? sceneId }
      : {};
    const row = { ...scene, ...withDefaults(made, MADE_DEFAULTS[collection]), createdAt: now, updatedAt: now } as Row;
    remember(collection, row);
    note(collection, [row]);
    return { response: json(row), url: url.href, body: JSON.stringify(made) };
  }

  function sendAgainLater(): void {
    clearTimeout(sendAgainTimer);
    sendAgainTimer = setTimeout(() => void sendWaiting(), sendAgainMs);
    sendAgainMs = Math.min(sendAgainMs * 2, MAX_SEND_AGAIN_MS);
  }

  // The server can't answer now: the saves waiting their turn are answered on the device, as every save is offline
  function standInForServer(): void {
    for (const save of waiting) {
      if (!save.inTurn || save === inFlight) continue;
      const { route, answer: reply } = save.inTurn;
      const url = new URL(save.url);
      delete save.inTurn;
      try {
        const local = answer(save.method, url, save.body, route);
        Object.assign(save, { url: local.url, body: local.body, standIn: true, touches: local.touches });
        reply(local.response);
      } catch {
        // A restore of a deletion the copy never knew: it waits its turn after all
        save.inTurn = { route, answer: reply };
      }
    }
  }

  // A batch the server refused for naming a row deleted meanwhile (`items[3].uniqueId`) is sent again without it
  async function withoutMissingItem(save: Waiting, response: Response): Promise<boolean> {
    if (response.status !== 404 || save.method !== 'POST' || !save.body || !/\/batch$/.test(new URL(save.url).pathname)) return false;
    const problem = await response.clone().json().catch(() => null) as { field?: unknown } | null;
    const missing = typeof problem?.field === 'string' ? /^items\[(\d+)\]/.exec(problem.field) : null;
    const sent = parse(save.body);
    const items = Array.isArray(sent.items) ? sent.items : [];
    if (!missing || Number(missing[1]) >= items.length) return false;
    const rest = items.filter((_, i) => i !== Number(missing[1]));
    if (rest.length === 0) return false;
    save.body = JSON.stringify({ ...sent, items: rest });
    return true;
  }

  // Sends the waiting saves one at a time, in order; one that gets no answer, or one that may pass, waits, with those behind it, to be sent again
  async function sendWaiting(): Promise<void> {
    checkUser();
    // Nothing left to send is nothing failing
    if (waiting.length === 0 && failing) {
      failing = false;
      changed();
    }
    if (sending || waiting.length === 0 || !connected()) return;
    sending = true;
    // Dropped meanwhile (someone else signed in): what the last user's sends answer changes nothing
    const dropsAtStart = drops;
    const droppedSince = () => drops !== dropsAtStart;
    clearTimeout(sendAgainTimer);
    try {
      while (waiting.length > 0) {
        if (!connected()) return;
        checkUser();
        if (droppedSince() || waiting.length === 0) return;
        const next = waiting[0];
        // A material made on the device is named by the id the server gave it
        const withServerIds = (text: string) => [...serverIds].reduce((out, [local, server]) => out.replaceAll(local, server), text);
        let response: Response;
        inFlight = next;
        try {
          response = await sendInTime(next, withServerIds(next.url), next.body === null ? null : withServerIds(next.body));
        } catch (error) {
          inFlight = null;
          if (droppedSince()) return;
          // Out of time: the next send of it gets longer
          if (isTimeout(error)) {
            console.warn(`[offlineSaves] ${next.method} ${next.url} got no answer within ${sendTimeoutMs} ms; it is sent again shortly`);
            sendTimeoutMs = Math.min(sendTimeoutMs * 2, MAX_SEND_TIMEOUT_MS);
          }
          // No answer: the save may not have reached the server, and sending it again is safe
          if (next.inTurn) {
            const { route, answer: reply } = next.inTurn;
            delete next.inTurn;
            try {
              const local = answer(next.method, new URL(next.url), next.body, route);
              Object.assign(next, { url: local.url, body: local.body, standIn: true, touches: local.touches });
              reply(local.response);
            } catch (failure) {
              // A restore of a deletion the copy never knew fails as it would have offline
              waiting.shift();
              finish(next, false);
              reply(failure instanceof Error ? failure : new TypeError('Failed to fetch'));
            }
          }
          failing = true;
          standInForServer();
          changed();
          sendAgainLater();
          return;
        }
        inFlight = null;
        if (droppedSince()) return;
        sendTimeoutMs = SEND_TIMEOUT_MS;
        if (await withoutMissingItem(next, response)) continue;
        if (droppedSince()) return;
        // An answer that may pass: a save already answered on the device is kept, to be sent again. One the server keeps
        // answering with the same fault is refused after all, so the saves behind it go
        if (!next.inTurn && mayPass(response.status)) {
          if (!refusedForGood(next, response.status)) {
            console.warn(`[offlineSaves] ${next.method} ${next.url} answered ${response.status}; it is sent again shortly`);
            failing = true;
            standInForServer();
            changed();
            sendAgainLater();
            return;
          }
          console.warn(`[offlineSaves] ${next.method} ${next.url} answered ${response.status} ${MAX_SERVER_FAULTS} times in a row; it is taken as refused`);
        }
        waiting.shift();
        failing = false;
        sendAgainMs = SEND_AGAIN_MS;
        madeOffline ||= next.offline;
        stoodIn ||= next.standIn || next.offline;
        // A delete of a row already gone did what it meant to
        const savedIt = response.ok || (!next.inTurn && next.method === 'DELETE' && response.status === 404);
        finish(next, savedIt);
        if (response.ok && provisional.size > 0 && routeOf(new URL(next.url))?.collection === 'material-instances') {
          for (const row of rowsIn(await response.clone().json().catch(() => null))) {
            for (const [local, target] of provisional) {
              if (row.assetInstanceUniqueId === target.assetInstanceUniqueId && row.targetNodeName === target.targetNodeName && row.targetNodeIndex === target.targetNodeIndex) {
                serverIds.set(local, row.uniqueId);
                provisional.delete(local);
              }
            }
          }
          if (droppedSince()) return;
        }
        const url = new URL(next.url);
        const route = routeOf(url);
        if (next.inTurn) {
          // Answered by the server, as a save sent at once is: its caller takes the answer, refused or not
          await learn(next.method, url, route, next.body, response.clone()).catch(error => console.warn('[offlineSaves] could not keep an answer', error));
          next.inTurn.answer(response);
          changed();
          continue;
        }
        if (savedIt) {
          saved += 1;
          // The server's own answer to a made or changed row (its defaults, its rounding) is kept, but for the fields the
          // saves still waiting change (a batch's, a placement's keyframes' and clips' too), which the copy already holds
          if (response.ok && (next.method === 'PATCH' || (next.method === 'POST' && route?.id === undefined))) {
            await learn(next.method, url, route, next.body, response.clone()).catch(error => console.warn('[offlineSaves] could not keep an answer', error));
          }
        } else {
          refused += 1;
          console.warn(`[offlineSaves] ${next.method} ${next.url} was refused with ${response.status}`);
        }
        changed();
      }
      synced$.next({ saved, refused, rows: [...answered], offline: madeOffline, reread: stoodIn });
      saved = 0;
      refused = 0;
      madeOffline = false;
      stoodIn = false;
      answered.clear();
      provisional.clear();
      serverIds.clear();
    } finally {
      // After a drop, sending is the next user's, started afresh
      if (!droppedSince()) {
        inFlight = null;
        sending = false;
        changed();
      }
    }
  }

  function enqueue(made: Omit<Waiting, 'seq' | 'maker'>): void {
    const save: Waiting = { ...made, maker: userId(), seq: ++madeCount };
    // A change to a row whose last change has not been sent yet goes with it: one PATCH, the newer fields last
    const last = waiting[waiting.length - 1];
    if (save.method === 'PATCH' && last && last !== inFlight && !last.inTurn && last.method === 'PATCH' && last.url === save.url
      && JSON.stringify(last.headers) === JSON.stringify(save.headers) && last.maker === save.maker) {
      last.body = JSON.stringify({ ...parse(last.body), ...parse(save.body) });
      for (const [at, fields] of save.touches) {
        const had = last.touches.get(at);
        last.touches.set(at, had === '*' || fields === '*' ? '*' : new Set([...(had ?? []), ...fields]));
      }
      last.offline ||= save.offline;
      last.standIn ||= save.standIn;
      last.seq = save.seq;
      // What the server made of the last body says nothing of this one
      delete last.faults;
    } else {
      waiting.push(save);
    }
    changed();
    if (connected() && !sending && !failing) void sendWaiting();
  }

  // The session's credentials are put on when it is sent, so a token that ran out meanwhile is refreshed then
  const headersOf = (request: Request) => [...request.headers.entries()].filter(([name]) => !['authorization', 'x-api-key'].includes(name.toLowerCase()));

  function keep(request: Request, url: URL, body: string | null, route: SaveRoute, send: Send, standIn: boolean): Response {
    const local = answer(request.method, url, body, route);
    const offline = !connected();
    enqueue({ method: request.method, url: local.url, headers: headersOf(request), body: local.body, send, touches: local.touches, offline, standIn: standIn || offline });
    return local.response;
  }

  function inTurn(request: Request, url: URL, body: string | null, route: SaveRoute, send: Send): Promise<Response> {
    return new Promise((resolve, reject) => {
      const reply = (answered: Response | Error) => (answered instanceof Error ? reject(answered) : resolve(answered));
      enqueue({ method: request.method, url: url.href, headers: headersOf(request), body, send, touches: touchesOf(request.method, route, body), offline: false, standIn: false, inTurn: { route, answer: reply } });
    });
  }

  return {
    async fetch(incoming, send) {
      checkUser();
      let request = incoming;
      const url = new URL(request.url);
      const route = routeOf(url);
      const save = isSave(request.method, route) && !!sceneId;
      // A delete takes its deletion id before it is first sent: one whose answer is lost is undone by the id the server recorded
      if (save && request.method === 'DELETE' && !url.searchParams.has('deletionUniqueId')) {
        url.searchParams.set('deletionUniqueId', crypto.randomUUID());
        request = new Request(url, request);
      }
      const body = save && request.method !== 'DELETE' ? await request.clone().text() : null;
      if (save) {
        const saveRoute = route as SaveRoute;
        if (answersAsServer(request.method, saveRoute)) return keep(request, url, body, saveRoute, send, false);
        if (unreachable()) return keep(request, url, body, saveRoute, send, true);
        // Answered by the server, in its turn: after every save made before it, and before every one made after
        return inTurn(request, url, body, saveRoute, send);
      }
      if (sceneId && request.method === 'GET' && ahead()) {
        const local = read(url, route, !connected());
        if (local) return local;
      }
      // Anything else reaches the server after the saves made before it; a change stream never waits
      if (waiting.length > 0 && !changeStream(request.url)) await whenSent();
      const dropsAtSend = drops;
      let response: Response;
      try {
        response = await send(request);
      } catch (error) {
        const local = sceneId && request.method === 'GET' ? read(url, route, true) : null;
        if (local) return local;
        throw error;
      }
      // An answer for the last user is never kept for the next
      if (drops === dropsAtSend) await learn(request.method, url, route, body, response.clone()).catch(error => console.warn('[offlineSaves] could not keep an answer', error));
      return response;
    },
    open(id) {
      sceneId = id;
    },
    setOnline(next) {
      online = next;
      if (!connected()) standInForServer();
      changed();
      if (connected()) void sendWaiting();
    },
    waiting$: waiting$.pipe(distinctUntilChanged()),
    stalled$: stalled$.pipe(distinctUntilChanged()),
    synced$: synced$.asObservable(),
    made: () => madeCount,
    landed(since) {
      return new Promise(resolve => {
        landings.push({ since, through: madeCount, resolve });
        settleLandings();
      });
    },
    sessionChanged() {
      checkUser();
    },
  };
}
